import { useState, useEffect, useCallback, useRef } from "react";
import { Formik, Form, FieldArray, useFormikContext } from "formik";
import "./Kid.css";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import BackButton from "../Goback/GoBack";
import { useAuth } from "../../../context/LoginContext/AuthContext";
import { useStoreKid } from "../../../../hooks/authV2/useStoreKid";
import { useGetPlan } from "../../../../hooks/authV2/useGetPlan";
import { KidDetailsForm } from "../../../WebUi/Components/AddStudent/KidDetailsForm";
import { AddKidButton } from "../../../WebUi/Components/AddStudent/ActionButtons";
import { UnlockBenefits } from "../../../WebUi/Components/AddStudent/UnlockBenefits";
import { AddStudentSkeleton } from "../../../WebUi/Components/AddStudent/AddStudentSkeleton";
import {
  INITIAL_KID_STATE,
  validationSchema,
} from "../../../WebUi/Pages/AddStudent/ValidationSchema.js";
import { getBillingPeriodLabel } from "../../../WebUi/Pages/AddStudent/helper";
import {
  savePaidParentDraft,
  loadPaidParentDraft,
  clearPaidParentDraft,
  getParentId,
} from "../../../../utils/studentDraftStorage";

/**
 * Subcomponent to auto-save paid parent draft kids debounced to localStorage.
 */
const FormikPaidParentDraftAutoSave = ({
  parentId,
  maxKidsAllowed,
  kidCount,
  kidSubscribed,
  tiersCount,
}) => {
  const { values } = useFormikContext();

  useEffect(() => {
    const pid = parentId || getParentId() || "guest";
    const timer = setTimeout(() => {
      savePaidParentDraft(pid, {
        kids: values.kids,
        maxKidsAllowed,
        kidCount,
        kidSubscribed,
        tiersCount,
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [values, parentId, maxKidsAllowed, kidCount, kidSubscribed, tiersCount]);

  return null;
};

export const ParentKidRegister = () => {
  const { kidCount, kidSubscribed } = useParams();
  const navigate = useNavigate();
  const viewItemFired = useRef(false);
  const { user } = useAuth();

  const parentId = user?.user?.id || user?.id || getParentId();
  const parentType = user?.user?.parent_type || user?.parent_type;
  const dashboardPath =
    parentType === "school_parent"
      ? "/school-parent/dashboard"
      : "/parent/dashboard";

  // Current counts from route params
  const currentKidCount = Number(kidCount) || 0;
  const currentKidSubscribed = Number(kidSubscribed) || 0;
  const maxKidsAllowed = Math.max(0, 5 - currentKidCount);
  const pendingPaymentCount = currentKidCount - currentKidSubscribed;

  const [initialKids, setInitialKids] = useState(() => {
    const pid = parentId || getParentId() || "guest";
    const storedDraft = loadPaidParentDraft(pid);
    if (
      storedDraft &&
      Array.isArray(storedDraft.kids) &&
      storedDraft.kids.length > 0
    ) {
      const storedAllowed = storedDraft.maxKidsAllowed;
      if (
        storedAllowed != null &&
        Number(storedAllowed) === Number(maxKidsAllowed)
      ) {
        return storedDraft.kids;
      }
    }
    return [{ ...INITIAL_KID_STATE }];
  });
  const [draftChecked, setDraftChecked] = useState(false);

  // Hook for POST /parent/store-kid
  const {
    storeKid,
    loading: isSubmittingApi,
    error: storeKidError,
    fieldErrors,
    setFieldErrors,
  } = useStoreKid();

  /**
   * Maps API error field paths to Formik field paths.
   * API uses fName/lName, Formik uses firstName/lastName.
   */
  const API_TO_FORMIK_FIELD_MAP = {
    fName: "firstName",
    lName: "lastName",
  };

  const mapApiFieldToFormik = (apiField) => {
    return apiField.replace(
      /kids\.(\d+)\.(\w+)/,
      (_, index, field) =>
        `kids.${index}.${API_TO_FORMIK_FIELD_MAP[field] || field}`,
    );
  };

  // Use the shared useGetPlan hook instead of inline fetch
  const {
    fetchPlans,
    loading: isFetchingPlans,
    data: plansData,
  } = useGetPlan();

  useEffect(() => {
    fetchPlans();

    // Check stored draft
    const pid = parentId || getParentId() || "guest";
    const storedDraft = loadPaidParentDraft(pid);

    if (
      storedDraft &&
      Array.isArray(storedDraft.kids) &&
      storedDraft.kids.length > 0
    ) {
      const storedAllowed = storedDraft.maxKidsAllowed;

      // When the number of kids we can add is greater or lesser than stored in localStorage,
      // completely erase that data and don't pre-fill (user bought or changed kids elsewhere)
      if (
        storedAllowed != null &&
        Number(storedAllowed) !== Number(maxKidsAllowed)
      ) {
        clearPaidParentDraft(pid);
        toast(
          "Your registered student allowance has changed from another session. Previous draft has been cleared.",
          { icon: "⚠️", duration: 6000 },
        );
        setInitialKids([{ ...INITIAL_KID_STATE }]);
      } else {
        setInitialKids(storedDraft.kids);
      }
    }

    setDraftChecked(true);
  }, [parentId, maxKidsAllowed]);

  // Derive plan data from the hook's response
  const plan = plansData?.[0] || null;
  const tiers = plan?.tiers || [];
  const currency = plan?.currency || "INR";
  const currencySymbol = currency === "INR" ? "₹" : "$";
  const billingPeriod = getBillingPeriodLabel(plan?.billing_cycle);

  // Store trial days in localStorage when plan loads
  useEffect(() => {
    if (plan?.trial_days) {
      localStorage.setItem("trial_days", plan.trial_days);
    }
  }, [plan]);

  // GTM / GA4 view_item event — preserved from original
  useEffect(() => {
    if (plansData?.length > 0 && !viewItemFired.current) {
      const items = plansData.map((p) => ({
        item_id: String(p.id),
        item_name: p.name,
        item_category: "YQ Yearly Subscription Plan",
        price: Number(p.final_price) || 0,
        currency: String(p.currency || "INR"),
        trial_days: p.trial_days,
        billing_cycle: p.billing_cycle,
        tiers: p.tiers,
        discount_percent: p.discount_percent,
        description: p.description,
      }));

      // Push to GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "view_item",
        currency: items[0].currency,
        value: items[0].price,
        items,
      });

      // Send to GA4 directly
      if (typeof window.gtag === "function") {
        window.gtag("event", "view_item", {
          currency: items[0].currency,
          value: items[0].price,
          items,
          debug_mode: true,
        });
        viewItemFired.current = true;
        console.log("✅ Sent view_item event to GA4:", {
          currency: items[0].currency,
          value: items[0].price,
          items,
        });
      }
    }
  }, [plansData]);

  /**
   * Returns the price for a kid at a given index based on the plan tiers.
   * Accounts for already-subscribed kids via currentKidSubscribed.
   */
  const getPriceForKid = useCallback(
    (index) => {
      if (!tiers.length) return 0;
      const tierIndex = Math.min(
        Number(index) + Number(currentKidSubscribed),
        tiers.length - 1,
      );
      return Number(tiers[tierIndex]?.price) || 0;
    },
    [tiers, currentKidSubscribed],
  );

  /**
   * Returns the "next kid" price shown on the Add Kid button.
   */
  const getNextKidPrice = useCallback(
    (currentCount) => {
      if (!tiers.length) return 0;
      const tierIndex = Math.min(
        Number(currentCount) + Number(currentKidSubscribed),
        tiers.length - 1,
      );
      return Number(tiers[tierIndex]?.price) || 0;
    },
    [tiers, currentKidSubscribed],
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      kids: values.kids.map(
        ({ firstName, lastName, age, email, username, password }) => ({
          fName: firstName,
          lName: lastName,
          age,
          ...(Number(age) < 13 && { username }),
          ...(Number(age) >= 13 && { email }),
          ...(Number(age) < 13 && { password }),
        }),
      ),
    };

    try {
      const result = await storeKid(payload);

      if (!result) {
        // fieldErrors will be set by the hook and applied via the render function
        return;
      }

      // Successful flow: clear draft from localStorage
      clearPaidParentDraft(parentId || getParentId());

      localStorage.setItem("number_of_kids", String(values.kids?.length));
      const studentIds = result?.kids?.map((kid) => kid.id).join(",") || "";
      localStorage.setItem("student_ids", studentIds);
      navigate("/parent/payment/update", {
        replace: true,
        state: {
          fromAddKid: true,
          createdKidNames: values.kids
            .map((k) => `${k.firstName} ${k.lastName}`)
            .join(", "),
          studentIds: studentIds,
        },
      });
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidatedSubmit = async (
    validateForm,
    setTouched,
    submitForm,
  ) => {
    const errors = await validateForm();
    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      const touchedFields = {};
      if (errors.kids) {
        touchedFields.kids = errors.kids.map((kidErrors) => {
          if (!kidErrors) return {};
          const touched = {};
          Object.keys(kidErrors).forEach((key) => {
            touched[key] = true;
          });
          return touched;
        });
      }
      setTouched(touchedFields);

      setTimeout(() => {
        const firstErrorEl = document.querySelector(".kidDetailsForm__error");
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    } else {
      submitForm();
    }
  };

  // Show skeleton while plans or draft check are loading
  if (isFetchingPlans || !draftChecked) {
    return (
      <section className="kidPage">
        <div className="kidPage__container">
          <div className="kidPage__backBtnWrap mb-3">
            <BackButton fallbackPath={dashboardPath} />
          </div>
          <AddStudentSkeleton />
        </div>
      </section>
    );
  }

  return (
    <section className="kidPage">
      <div className="kidPage__container">
        <div className="kidPage__backBtnWrap mb-3">
          <BackButton fallbackPath={dashboardPath} />
        </div>
        <UnlockBenefits />

        {pendingPaymentCount > 0 && (
          <div className="alert alert-info d-flex flex-column flex-md-row justify-content-between align-items-center rounded-4 shadow-sm p-3 mb-4 gap-3">
            <div className="d-flex align-items-center gap-2">
              <i className="fa-solid fa-circle-info fs-5 text-primary"></i>
              <div>
                <strong>Notice:</strong> You have {pendingPaymentCount} kid
                {pendingPaymentCount > 1 ? "s" : ""} already registered with payment pending.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-semibold text-nowrap"
              onClick={() => navigate(dashboardPath)}
            >
              View on Dashboard & Pay
            </button>
          </div>
        )}

        {/* Formik Form */}
        <Formik
          initialValues={{ kids: initialKids }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            isSubmitting,
            validateForm,
            setTouched,
            submitForm,
            setFieldError,
          }) => {
            const canAddMore = values.kids.length < maxKidsAllowed;
            const remainingSlots = maxKidsAllowed - values.kids.length;
            const isFormDisabled = isSubmitting || isSubmittingApi;

            // Apply field-level API errors to Formik fields (same pattern as AddStudentPage)
            if (fieldErrors) {
              queueMicrotask(() => {
                Object.entries(fieldErrors).forEach(([key, messages]) => {
                  if (Array.isArray(messages) && messages.length > 0) {
                    const formikKey = mapApiFieldToFormik(key);
                    setFieldError(formikKey, messages[0]);
                  }
                });
                // Clear after applying so they don't re-apply on every render
                setFieldErrors(null);
              });
            }

            return (
              <Form>
                <FormikPaidParentDraftAutoSave
                  parentId={parentId || getParentId()}
                  maxKidsAllowed={maxKidsAllowed}
                  kidCount={currentKidCount}
                  kidSubscribed={currentKidSubscribed}
                  tiersCount={tiers.length}
                />
                <div className="kidPage__forms">
                  <FieldArray name="kids">
                    {({ push, remove }) => (
                      <>
                        {values.kids.map((kid, index) => (
                          <KidDetailsForm
                            key={`kid-${index}`}
                            index={index}
                            price={getPriceForKid(index)}
                            currencySymbol={currencySymbol}
                            billingPeriod={billingPeriod}
                            showRemove={values.kids.length > 1}
                            canRemove={true}
                            kidAge={kid.age}
                            status="new"
                            disabled={isFormDisabled}
                            onRemove={() => remove(index)}
                          />
                        ))}

                        <AddKidButton
                          price={getNextKidPrice(values.kids.length)}
                          currencySymbol={currencySymbol}
                          billingPeriod={billingPeriod}
                          disabled={!canAddMore || isFormDisabled}
                          remainingSlots={remainingSlots}
                          onAdd={() => {
                            if (
                              values.kids.length +
                                Number(currentKidCount) <
                              5
                            ) {
                              push({ ...INITIAL_KID_STATE });
                            } else {
                              toast.error("You can only have up to 5 kids.");
                            }
                          }}
                        />
                      </>
                    )}
                  </FieldArray>
                </div>

                {storeKidError && (
                  <div
                    className="kidPage__apiError"
                    style={{
                      color: "red",
                      margin: "15px 0",
                      textAlign: "center",
                    }}
                  >
                    {storeKidError}
                  </div>
                )}

                <div className="kidPage__submitWrap">
                  <button
                    type="button"
                    className="kidPage__submitBtn"
                    disabled={isFormDisabled}
                    onClick={() =>
                      handleValidatedSubmit(
                        validateForm,
                        setTouched,
                        submitForm,
                      )
                    }
                  >
                    {isFormDisabled ? "Processing..." : "Submit"}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </section>
  );
};
